import React, { useState, useCallback, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppStoreContext } from '../../context/appStoreContext';
import { useSessionContext } from '../../context/sessionContext';
import { SUPPORT_EMAIL_ID } from '../../utils/helper';
import { fetchPlanUsageDetails } from '../../utils/services/planUsage';
import SetupErrorModal from '../../components/Modals/SetupErrorModal';
import UpgradePlanModal from '../../components/Modals/UpgradePlanModal';
import { CREATE_NEW_TEMPLATE_MODAL } from '../../components/CreateNewTemplate/translationValues';
import { Icon } from 'antd';
import { NEW_EMAIL_TEMPLATE_SECTION } from './translationValues';
import CreateComplexMailTemplateModal from '../../components/CreateNewTemplate/CreateComplexMailTemplateModal';
import CreateStandaloneReusableSection from '../../components/Modals/CreateStandaloneReusableSection';
import {
  TopBarContainer,
  TopSection,
  Title,
  Subtitle,
  BottomSection,
  ButtonGroup,
  ButtonWrapper,
  PlusButton,
  TopBarViewButton,
} from './../NewTemplateSection/styles';

const NAMESPACES = ['CREATE_NEW_TEMPLATE_MODAL', 'NEW_EMAIL_TEMPLATE_SECTION'];

const TopBar = ({ selectedView, onViewChange, sidebarCollapsed }) => {
  const { t } = useTranslation(NAMESPACES);

  const titleMap = {
    emails: {
      title: t(NEW_EMAIL_TEMPLATE_SECTION.EMAILS.key, NEW_EMAIL_TEMPLATE_SECTION.EMAILS.value),
      description: t(
        NEW_EMAIL_TEMPLATE_SECTION.EMAILS_DESC.key,
        NEW_EMAIL_TEMPLATE_SECTION.EMAILS_DESC.value,
      ),
    },
    reusable_sections: {
      title: t(
        NEW_EMAIL_TEMPLATE_SECTION.REUSABLE_SECTIONS.key,
        NEW_EMAIL_TEMPLATE_SECTION.REUSABLE_SECTIONS.value,
      ),
      description: t(
        NEW_EMAIL_TEMPLATE_SECTION.REUSABLE_SECTIONS_DESC.key,
        NEW_EMAIL_TEMPLATE_SECTION.REUSABLE_SECTIONS_DESC.value,
      ),
    },
  };

  const [showModal, setShowModal] = useState(null); // emails | reusable_sections
  const [isCheckingLimit, setIsCheckingLimit] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [planName, setPlanName] = useState('');
  const [documentLimit, setDocumentLimit] = useState(null);
  const [showSetupFailureError, setShowSetupFailureError] = useState(false);

  const history = useHistory();
  const location = useLocation();

  const { isEnterprise, checkUserAccess, checkUserAccessRoles } = useContext(AppStoreContext);

  const organisationSelector = useCallback(store => store.organizationSetup, []);
  const [organizationSetup] = useSessionContext(organisationSelector);

  const isEmailContent = location.pathname === '/emailTemplates';
  const isSaasSetupFailed =
    !isEnterprise && checkUserAccess('client') && organizationSetup !== 'completed';

  const redirectToMailTemplate = template => {
    if (template.templateId) {
      history.push(`/complexEmailDesign/${template.templateId}`);
    }
  };

  const handleCreateDocTemplate = async () => {
    if (isSaasSetupFailed) {
      return setShowSetupFailureError(true);
    }
    if (isEnterprise || checkUserAccess('master')) {
      setShowModal('emails');
      return;
    }
    try {
      setIsCheckingLimit(true);
      const response = await fetchPlanUsageDetails();
      if (response?.statusCode === 1918) {
        const { templateLimit: created } = response.data.usage;
        const { templateLimit: limit, planName } = response.data.upperLimit;
        if (created >= limit) {
          setDocumentLimit(limit);
          setPlanName(planName);
          setShowUpgradeModal(true);
        } else {
          setShowModal('emails');
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsCheckingLimit(false);
    }
  };

  return (
    <TopBarContainer>
      <TopSection sidebarCollapsed={sidebarCollapsed}>
        <div>
          <Title>{titleMap[selectedView].title}</Title>
          <Subtitle>{titleMap[selectedView].description}</Subtitle>
        </div>
      </TopSection>

      <BottomSection sidebarCollapsed={sidebarCollapsed}>
        <ButtonGroup>
          <ButtonWrapper className="group">
            <PlusButton className="plus-button">
              {isCheckingLimit ? (
                <Icon type="loading" />
              ) : (
                <Icon
                  type="plus"
                  style={{ fontSize: 14 }}
                  onClick={() => {
                    if (isEmailContent) {
                      handleCreateDocTemplate();
                    } else {
                      setShowModal('emails');
                    }
                  }}
                />
              )}
            </PlusButton>
            <TopBarViewButton
              active={selectedView === 'emails'}
              view="emails"
              onClick={() => onViewChange('emails')}
            >
              {t(NEW_EMAIL_TEMPLATE_SECTION.EMAILS.key, NEW_EMAIL_TEMPLATE_SECTION.EMAILS.value)}
            </TopBarViewButton>
          </ButtonWrapper>

          <ButtonWrapper className="group">
            {checkUserAccessRoles(['REUSABLECONTENTMANAGER']) && (
              <PlusButton className="plus-button">
                <Icon
                  type="plus"
                  style={{ fontSize: 14 }}
                  onClick={() => {
                    if (isSaasSetupFailed) setShowSetupFailureError(true);
                    else setShowModal('reusable_sections');
                  }}
                />
              </PlusButton>
            )}
            <TopBarViewButton
              active={selectedView === 'reusable_sections'}
              view="reusable_sections"
              onClick={() => onViewChange('reusable_sections')}
            >
              {t(
                NEW_EMAIL_TEMPLATE_SECTION.REUSABLE_SECTIONS.key,
                NEW_EMAIL_TEMPLATE_SECTION.REUSABLE_SECTIONS.value,
              )}
            </TopBarViewButton>
          </ButtonWrapper>
        </ButtonGroup>
      </BottomSection>

      {showModal === 'emails' && (
        <CreateComplexMailTemplateModal
          visible={true}
          onCancel={() => setShowModal(null)}
          onSucess={redirectToMailTemplate}
          categoryType="template"
        />
        
      )}
      {showModal === 'reusable_sections' && (
        <CreateStandaloneReusableSection
          visible={true}
          onCancel={() => setShowModal(null)}
          categoryType="template"
          isEmailElements={true}
          isReferenced={true}
          isEmailReferencedBlock={true}
        />
      )}

      <UpgradePlanModal
        visible={showUpgradeModal}
        modalText={
          planName === 'Premium'
            ? t(
              CREATE_NEW_TEMPLATE_MODAL.PREMIUM_DOCUMENT_CREATION_LIMIT_EXCEEDED.key,
              CREATE_NEW_TEMPLATE_MODAL.PREMIUM_DOCUMENT_CREATION_LIMIT_EXCEEDED.value,
              { emailId: SUPPORT_EMAIL_ID },
            )
            : planName === 'Free'
              ? t(
                CREATE_NEW_TEMPLATE_MODAL.FREE_PLAN_UPGRADE_MESSAGE.key,
                CREATE_NEW_TEMPLATE_MODAL.FREE_PLAN_UPGRADE_MESSAGE.value,
                { documentLimit },
              )
              : t(
                CREATE_NEW_TEMPLATE_MODAL.STANDARD_PLAN_UPGRADE_MESSAGE.key,
                CREATE_NEW_TEMPLATE_MODAL.STANDARD_PLAN_UPGRADE_MESSAGE.value,
                { documentLimit },
              )
        }
        onCancel={() => setShowUpgradeModal(false)}
      />

      <SetupErrorModal showError={showSetupFailureError} setShowError={setShowSetupFailureError} />
    </TopBarContainer>
  );
};

export default TopBar;
